"use client";

import { useRouter } from "next/navigation";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { getQuoteReviewSummaryEditSection } from "@/app/common-utils/edit-quote-review-summary-flow.utils";
import CookiePolicyButton from "@/app/shared-components/cookie-policy-button/cookie-policy-button";
import CountryChangeModal from "@/app/shared-components/country-change-modal/country-change-modal";
import HelpOverLayButton from "@/app/shared-components/help-overlay-button/help-overlay-button-wizard";
import QuestionFooterSection from "@/app/shared-components/question-footer-section";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import Logo from "@/components/molecules/logo-component/logo";
import PromoCodeBanner from "@/components/molecules/promo-code-banner";
import PromoCodeBannerWithTitle from "@/components/molecules/promo-code-banner/promo-code-banner-with-title";
import AcoSection from "@/components/organisms/quote/acos/aco-section";
import AtmToggleBottom from "@/components/organisms/quote/amt-toggle-bottom/atm-toggle-bottom";
import PriceAndDocs from "@/components/organisms/quote/price-and-docs/price-and-docs";
import QuoteCover from "@/components/organisms/quote/quote-cover/quote-cover";
import QuoteHeader from "@/components/organisms/quote/quote-header";
import withQuoteData from "@/components/with-quote-data";
import { AnnualMultiTrip, SingleTrip } from "@/config/app-constant";
import {
  isSTSUK,
  BRAND_STAYSURE,
  PAY_AT_ONCE,
  PRODUCT,
  SPREAD_THE_COST,
} from "@/constants";
import useQuoteSubmit from "@/hooks/api/quote/quote-submit";
import useGtmPageView from "@/hooks/gtm/use-gtm-page-view";
import useGtmQuotePolicyOptions from "@/hooks/gtm/use-gtm-quote-policy-options";
import useCustomPageTracking from "@/hooks/page-tracking/use-custom-page-tracking";
import useUpdateProgressBar from "@/hooks/progress/use-update-progress-bar";
import useMediaQuery from "@/hooks/use-media-query/use-media-query-enhanced";
import { usePromoBanner } from "@/hooks/use-promo-banner";
import { useQuoteDisabled } from "@/hooks/use-quote-disabled/use-quote-disabled";
import useWindowSize from "@/hooks/use-window-size/use-window-size";
import { useLoading } from "@/providers/loading-provider";
import { RootState } from "@/store/root-reducer";
import { setApiError } from "@/store/slices/errors/api-error.slice";
import {
  clearQuote,
  setTripTypeChangedState,
  updateQuote,
  updateQuoteCompleted,
} from "@/store/slices/quote/quote.slice";
import { PaymentConsent } from "@/types/quote-data";
import { PayloadType } from "@/types/QuoteSubmitTypes";
import { TripDetails } from "@/types/TripDetails";
import { apiErrorMappings } from "@/utils/errors/api-error-map";
import QuoteCoverTypeMap from "@/utils/gtm/cover-type-map";
import QuoteDataMap from "@/utils/gtm/quote-data-map";
import { hidePersonalDetailsFields } from "@/utils/organiser.utils";
import {
  AcoSectionContentType,
  PromoBannerContentType,
  partialMedicalDeclineModalContentType,
  PtmExitModalType,
} from "@/utils/schema/quote-page-schema.utils";

import Styles from "../../app/home-page.module.css";
import BtnLoadingSvg from "../atoms/btn-loading-svg/btn-loading-svg";
import ActionButton from "../molecules/contact-details/action-button/action-button";
import FloatingChatButton from "../molecules/floating-chat-button/floating-chat-button";
import PartialMedicalDeclineModal from "../molecules/quote-section/partial-medical-decline-modal/partial-medical-decline-modal";
import InterstitialPageContent from "../organisms/quote/interstitial/interstitial-loading-page-content";
import PtmExitModalWrapper from "../organisms/quote/ptm-exit-modal/ptm-exit-modal-wrapper";
import Style from "../organisms/quote/quote-page.module.css";

interface ClientComponentProps {
  headerContent: any;
  tripDetailsBottomAreaContent: any;
  toggleModalContent: any;
  quoteCoverContent: any;
  drawerData: any;
  quoteAreaData: any;
  acoSectionContent: AcoSectionContentType | null;
  priceAndDocAreaContent: any;
  promoBannerContent?: PromoBannerContentType | null;
  partialMedicalDeclineModalContent?: partialMedicalDeclineModalContentType | null;
  ctaLabel?: string;
  chatButtonLabelDesktop?: string;
  chatButtonLabelMobile?: string;
  ptmExitModalContent?: PtmExitModalType;
}

function QuotePage({
  headerContent,
  tripDetailsBottomAreaContent,
  toggleModalContent,
  quoteCoverContent,
  drawerData,
  quoteAreaData,
  acoSectionContent,
  priceAndDocAreaContent,
  promoBannerContent,
  partialMedicalDeclineModalContent,
  ctaLabel,
  chatButtonLabelDesktop,
  chatButtonLabelMobile,
  ptmExitModalContent,
}: Readonly<ClientComponentProps>) {
  const pageName = "quote";
  const router = useRouter();
  const dispatch = useDispatch();
  const { current, wizard } = useWizard();
  const { productSpecialFeatures } = wizard;
  const { showLoading, hideLoading } = useLoading();

  // Compute navigation behavior flag once from sessionStorage
  const navigateBeforeApiComplete = useMemo(
    () =>
      sessionStorage.getItem("navigateToQuoteBeforeContactDetailsApi") ===
      "true",
    []
  );
  const quoteData = useSelector((state: RootState) => state.quote);
  const { attributes, referenceId, quoteCompleted, isTripTypeChanged } =
    quoteData;
  const { priceResult, tripDetails, paymentDetails } = attributes;
  // Track previously declined covers to detect NEW declines
  // Initialize from sessionStorage if available
  const [previousDeclinedCovers, setPreviousDeclinedCovers] = useState<
    Set<string>
  >(() => {
    const stored = sessionStorage.getItem("previousDeclinedCovers");
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });

  // Show modal if there are NEW declined covers or user came from medical flow with USPD
  const showPartialMedicalDeclineModal = useMemo(() => {
    if (!priceResult?.policyPackagePrice) {
      return false;
    }

    const currentPolicyType = attributes.tripDetails?.policyType;
    const coversForCurrentType = priceResult.policyPackagePrice.filter(
      (price) => price.policyType === currentPolicyType
    );

    // Get currently declined cover levels (use policyPackage as unique identifier)
    const currentlyDeclined = new Set(
      coversForCurrentType
        .filter((price) => price.hasMedicalDeclined === true)
        .map((price) => price.policyPackage)
        .filter(Boolean) // Remove any undefined/null values
    );

    // No declined covers, don't show modal
    if (currentlyDeclined.size === 0) {
      return false;
    }

    // Check if this is first visit (no previousDeclinedCovers in sessionStorage)
    const isFirstVisit = !sessionStorage.getItem("previousDeclinedCovers");

    // On first visit, don't show modal for existing declined covers
    if (isFirstVisit) {
      return false;
    }

    // Check if there are NEW declined covers (not in previous set)
    const hasNewDeclines = Array.from(currentlyDeclined).some(
      (coverCode) => !previousDeclinedCovers.has(coverCode)
    );

    // Show modal if there are new declines and not in initial loading state
    return hasNewDeclines;
    // return hasNewDeclines && !tripDetails?.initialQuotePageLoading;
  }, [
    attributes.tripDetails?.policyType,
    priceResult?.policyPackagePrice,
    previousDeclinedCovers,
    tripDetails?.initialQuotePageLoading,
  ]);
  const { isPromoBannerActive } = usePromoBanner(
    promoBannerContent?.promoCode,
    promoBannerContent?.bannerStatus
  );
  const [showInterstitial, setShowInterstitial] = React.useState(() => {
    // Only show interstitial if flag is enabled AND coming from contact-details
    const comingFromContactDetails =
      sessionStorage.getItem("isSession") !== "true";
    const shouldNavigateBeforeApi =
      sessionStorage.getItem("navigateToQuoteBeforeContactDetailsApi") ===
      "true";
    return shouldNavigateBeforeApi && comingFromContactDetails;
  });
  const isInitialMount = React.useRef(true);
  const interstitialStartTime = React.useRef<number | null>(null);
  const paymentConsent = useSelector(
    (state: RootState) => state.paymentConsents
  );

  const isQuoteDisabled = useQuoteDisabled();
  const isLoadingCoverState = useSelector(
    (state: RootState) => state.quote.isLoadingCoverState
  );

  const [continueIsLoading, setContinueIsLoading] = useState(false);
  const extrasSectionRef = useRef<HTMLDivElement>(null);

  const { pageReferrer, pageUrl, pageTitle } = useCustomPageTracking(pageName);
  useGtmPageView({
    userId: "",
    quoteId: "",
    pageReferrer,
    pageUrl,
    pageTitle,
  });

  let isEligible = false;
  if (paymentDetails) {
    const { paymentOptions } = paymentDetails;
    const [
      {
        cardPayment: {
          premiumCreditPayment: [{ pcl }],
        },
      },
    ] = paymentOptions;

    isEligible = pcl.eligible;
  }

  const isTaxCopyBelowButton =
    current?.brandSpecificParams?.isTaxCopyBelowDetailedComparisonBtn === true;

  useUpdateProgressBar(current?.progressBarPercentage ?? -1);
  const headerRef = useRef<HTMLDivElement>(null);
  const quoteCoverRef = useRef<HTMLDivElement>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const continueBtnRef = useRef<HTMLDivElement>(null);
  const windowSize = useWindowSize();
  const [isMindesktop, isReadyMinDesktop] = useMediaQuery("(min-width: 904px)");

  const gtmQuoteDataViewItems = QuoteDataMap(
    attributes,
    "view_item_list",
    "cover selection",
    referenceId
  );
  const [initiateGtmUpdate, setInitiateGtmUpdate] = useState(false);
  const [isdesktop, isReady] = useMediaQuery("(min-width: 600px)");
  const [isMobile, isReadyMobile] = useMediaQuery("(max-width: 599px)");

  useEffect(() => {
    sessionStorage.removeItem("isRouteFromMedicalDashBoard");
  }, []);

  // Initialize previousDeclinedCovers on first visit (when not in sessionStorage)
  useEffect(() => {
    const isFirstVisit = !sessionStorage.getItem("previousDeclinedCovers");

    if (priceResult?.policyPackagePrice && isFirstVisit) {
      const currentPolicyType = attributes.tripDetails?.policyType;
      const coversForCurrentType = priceResult.policyPackagePrice.filter(
        (price) => price.policyType === currentPolicyType
      );

      const currentlyDeclined = new Set(
        coversForCurrentType
          .filter((price) => price.hasMedicalDeclined === true)
          .map((price) => price.policyPackage)
          .filter(Boolean)
      );

      // Initialize in both state and sessionStorage
      setPreviousDeclinedCovers(currentlyDeclined);
      sessionStorage.setItem(
        "previousDeclinedCovers",
        JSON.stringify(Array.from(currentlyDeclined))
      );
    }
  }, [priceResult?.policyPackagePrice, attributes.tripDetails?.policyType]);

  // Handle modal close - save declined covers to prevent re-showing
  // Only saves when user actually closes the modal
  const handleModalClose = useCallback(() => {
    if (priceResult?.policyPackagePrice) {
      const currentPolicyType = attributes.tripDetails?.policyType;
      const coversForCurrentType = priceResult.policyPackagePrice.filter(
        (price) => price.policyType === currentPolicyType
      );

      const currentlyDeclined = new Set(
        coversForCurrentType
          .filter((price) => price.hasMedicalDeclined === true)
          .map((price) => price.policyPackage)
      );

      setPreviousDeclinedCovers(currentlyDeclined);

      // Persist to sessionStorage
      sessionStorage.setItem(
        "previousDeclinedCovers",
        JSON.stringify(Array.from(currentlyDeclined))
      );
    }
  }, [priceResult?.policyPackagePrice, attributes.tripDetails?.policyType]);

  // Store promo banner status in sessionStorage for use by child components
  useEffect(() => {
    if (promoBannerContent?.bannerStatus) {
      sessionStorage.setItem(
        "promoBannerStatus",
        promoBannerContent.bannerStatus
      );
    } else {
      // If no content from CMS, explicitly set to disabled to prevent fallback to localStorage
      sessionStorage.setItem("promoBannerStatus", "disabled");
    }
  }, [promoBannerContent?.bannerStatus]);

  // Scroll to extras section if user clicked "Edit extras" from quote-review
  useEffect(() => {
    const editSection = getQuoteReviewSummaryEditSection();

    if (editSection === "extras") {
      // Wait for the extras section to be rendered (after tripDetails loads)
      const checkAndScroll = () => {
        if (extrasSectionRef.current) {
          // Element is now available, scroll to it
          extrasSectionRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        } else {
          // Element not ready yet, check again
          setTimeout(checkAndScroll, 100);
        }
      };

      // Start checking after initial render
      setTimeout(checkAndScroll, 500);
    }
  }, [tripDetails]);

  const mobileStickyTop = useMemo(() => {
    if (isMobile && continueBtnRef?.current?.getBoundingClientRect().y) {
      return continueBtnRef?.current?.getBoundingClientRect().y - 136;
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowSize, isMobile, isReadyMobile, continueBtnRef.current]);

  const policyPackagesViewItems = QuoteCoverTypeMap(attributes);
  if (gtmQuoteDataViewItems.ecommerce) {
    gtmQuoteDataViewItems.ecommerce.item_list_id = "recommended_policies";
    gtmQuoteDataViewItems.ecommerce.item_list_name = "recommended_policies";
  }
  delete gtmQuoteDataViewItems.ecommerce?.value;

  const isDataReady =
    policyPackagesViewItems.length > 0 &&
    Object.keys(gtmQuoteDataViewItems).length > 0;

  // gtm function for page load
  useGtmQuotePolicyOptions({
    quoteData: gtmQuoteDataViewItems,
    policyItems: policyPackagesViewItems,
    isReady: isDataReady,
  });

  let gtmQuoteData = null;
  let policyPackages = null;
  if (quoteData) {
    gtmQuoteData = QuoteDataMap(
      quoteData.attributes,
      "add_to_cart",
      "cover selection",
      quoteData.referenceId
    );
    policyPackages = QuoteCoverTypeMap(quoteData.attributes);
    delete policyPackages[0]?.index;
  }

  useGtmQuotePolicyOptions({
    quoteData: gtmQuoteData,
    policyItems: policyPackages,
    isReady: initiateGtmUpdate,
  });

  const generatePayload = (
    tripData: TripDetails | null | undefined,
    paymentOptionType: PaymentConsent
  ): PayloadType => {
    const stAmtUpsellFlow = localStorage.getItem("stAmtUpsellFlow");
    const experimentDetails = stAmtUpsellFlow
      ? {
          ...quoteData.attributes.experimentDetails,
          stAmtUpsellFlow,
        }
      : null;

    if (tripData?.policyType === SingleTrip) {
      return {
        paymentOption: {
          selectedPaymentOption: PAY_AT_ONCE,
        },
        experimentDetails,
      };
    }
    if (tripData?.policyType === AnnualMultiTrip && !isEligible) {
      return {
        paymentOption: {
          selectedPaymentOption: PAY_AT_ONCE,
        },
        experimentDetails,
      };
    }
    return {
      paymentOption: {
        selectedPaymentOption:
          paymentOptionType.type === SPREAD_THE_COST
            ? SPREAD_THE_COST
            : PAY_AT_ONCE,
      },
      experimentDetails,
    };
  };

  const resetTrigger = useCallback(() => {
    setContinueIsLoading(false);
    hideLoading();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { responseCode, loading, error, quoteSubmit, newQuote } =
    useQuoteSubmit(resetTrigger);

  // Memoized values for previous, next, and fallback pages
  const prevPage = useMemo(() => {
    return current?.prev;
  }, [current?.prev]);

  const experimentDetails = quoteData?.attributes.experimentDetails;

  const nextPage = useMemo(() => {
    // If user is in variant flow, go to required information page
    if (hidePersonalDetailsFields(experimentDetails)) {
      return "/informacion-requerida";
    }
    // Otherwise, use the default next page
    return current?.next;
  }, [current?.next, experimentDetails]);

  const fallbackPageOnError = useMemo(() => {
    return current?.edges?.fallbackOnError;
  }, [current?.edges?.fallbackOnError]);

  // Handlers for navigate previous, next, and fallback pages
  const prevOnClickHandler = useCallback(() => {
    if (prevPage) {
      router.push(prevPage);
    }
  }, [prevPage, router]);

  const gotoNextPage = useCallback(() => {
    if (nextPage) {
      router.push(nextPage);
    }
  }, [nextPage, router]);

  const gotoFallbackPage = useCallback(() => {
    if (fallbackPageOnError) {
      router.push(fallbackPageOnError);
    }
  }, [fallbackPageOnError, router]);

  useEffect(() => {
    if (tripDetails?.initialQuotePageLoading) {
      dispatch(updateQuoteCompleted({ completed: false }));
    } else if (
      tripDetails?.initialQuotePageLoading === false &&
      tripDetails?.policyType === SingleTrip
    ) {
      dispatch(updateQuoteCompleted({ completed: true }));
    } else if (
      tripDetails?.initialQuotePageLoading === false &&
      tripDetails?.policyType === AnnualMultiTrip &&
      !isEligible
    ) {
      dispatch(updateQuoteCompleted({ completed: true }));
    } else if (
      paymentDetails?.paymentOption !== null &&
      paymentDetails?.paymentOption !== undefined
    ) {
      dispatch(updateQuoteCompleted({ completed: true }));
    } else if (
      !(paymentConsent?.type == null || paymentConsent?.type === "") &&
      tripDetails?.policyType === AnnualMultiTrip
    ) {
      dispatch(updateQuoteCompleted({ completed: true }));
    } else {
      dispatch(updateQuoteCompleted({ completed: false }));
    }
  }, [
    tripDetails,
    attributes,
    dispatch,
    isEligible,
    paymentDetails,
    paymentConsent,
  ]);

  useEffect(() => {
    const handleEffect = async () => {
      if (!loading && !error && responseCode === 200) {
        dispatch(updateQuote(newQuote!));
        gotoNextPage();
      }

      if (!loading && error !== null) {
        if (error.response?.status === 401) {
          dispatch(clearQuote());
          gotoFallbackPage();
        } else {
          const errorObject = await apiErrorMappings(error);
          errorObject.multiButton = true;
          dispatch(setApiError(errorObject));
        }
      }
    };
    handleEffect();
  }, [
    responseCode,
    loading,
    error,
    dispatch,
    router,
    newQuote,
    gotoNextPage,
    gotoFallbackPage,
  ]);

  // Track when interstitial starts showing
  useEffect(() => {
    if (showInterstitial && interstitialStartTime.current === null) {
      interstitialStartTime.current = Date.now();
    }
  }, [showInterstitial]);

  useEffect(() => {
    if (referenceId) {
      hideLoading();

      // If we're showing interstitial (coming from contact-details with flag enabled)
      if (
        showInterstitial &&
        isInitialMount.current &&
        navigateBeforeApiComplete
      ) {
        // New flow: Show interstitial for minimum 5 seconds
        const MINIMUM_DISPLAY_TIME = PRODUCT === BRAND_STAYSURE ? 6000 : 5000; // 5 seconds
        const startTime = interstitialStartTime.current || Date.now();
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, MINIMUM_DISPLAY_TIME - elapsedTime);

        // Hide interstitial after minimum time has passed
        const timer = setTimeout(() => {
          setShowInterstitial(false);
          isInitialMount.current = false;
          // Mark as in session to prevent interstitial on refresh
          sessionStorage.setItem("isSession", "true");
        }, remainingTime);

        return () => clearTimeout(timer);
      } else if (!showInterstitial) {
        // Not showing interstitial, mark as no longer initial mount
        isInitialMount.current = false;
        // Mark as in session to prevent interstitial on refresh
        sessionStorage.setItem("isSession", "true");
      }
    } else {
      showLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceId, showInterstitial, navigateBeforeApiComplete]);

  useEffect(() => {
    if (isTripTypeChanged) {
      const topOffset = headerRef?.current?.offsetTop;
      const heightOffset = headerRef?.current?.getBoundingClientRect().height;
      window.scrollTo({
        top: topOffset && heightOffset ? topOffset + heightOffset : 0,
        behavior: "smooth",
      });
    }
    dispatch(setTripTypeChangedState(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTripTypeChanged]);

  const continueHandleClick = async () => {
    const payLoad: PayloadType = generatePayload(tripDetails, paymentConsent);
    setContinueIsLoading(true);
    showLoading();
    quoteSubmit(payLoad);
    setInitiateGtmUpdate(true);
  };

  const mainStyle = `max-w-[1200px] mx-auto my-0 w-[100%] relative lg:py-spacing-xxl md:py-spacing-xl
  py-spacing-l lg:pb-spacing-xxl md:pb-spacing-xl pb-spacing-5xl`;

  // Calculate padding bottom class based on brand and header style
  let contentBoxPaddingBottom: string;
  if (isSTSUK) {
    contentBoxPaddingBottom = "md:pb-spacing-m";
  } else if (current?.uiElementControls?.internationalHeaderStyle) {
    contentBoxPaddingBottom = "lg:pb-spacing-xl md:pb-spacing-m";
  } else {
    contentBoxPaddingBottom = "md:pb-spacing-xxl";
  }
  // Check if AtmToggleBottom should be hidden (when policy type is ST and all covers are declined)
  const isSingleTrip = tripDetails?.policyType === SingleTrip;
  const allAMTCoversDeclined =
    priceResult?.policyPackagePrice
      ?.filter((price) => price.policyType === AnnualMultiTrip)
      .every((price) => price.hasMedicalDeclined === true) ?? false;
  const shouldHideAtmToggle = isSingleTrip && allAMTCoversDeclined;

  if (!isReady || !isReadyMinDesktop || !isReadyMobile) return null;
  return (
    <div
      data-testid="quotePage"
      className={`${Styles.homepageWrapper} bg-surface-neutral-option-2 !h-[100%]`}
    >
      {productSpecialFeatures?.showCountryFooter && <CountryChangeModal />}
      <PartialMedicalDeclineModal
        partialMedicalDeclineModalContent={partialMedicalDeclineModalContent}
        showModal={showPartialMedicalDeclineModal}
        onClose={handleModalClose}
      />
      <div className={mainStyle}>
        <div className=" w-full h-full">
          <main className=" lg:pb-spacing-xxl md:pb-spacing-xl pb-spacing-l">
            {tripDetails?.initialQuotePageLoading !== true &&
              referenceId &&
              gridWrapperRef?.current?.getBoundingClientRect() && (
                <div
                  style={{
                    ...(isMindesktop &&
                    gridWrapperRef?.current?.getBoundingClientRect().right
                      ? {
                          left: `${
                            gridWrapperRef?.current?.getBoundingClientRect()
                              .right - 140
                          }px`,
                        }
                      : {}),
                    ...(!isMindesktop &&
                    !isMobile &&
                    gridWrapperRef?.current?.getBoundingClientRect().right
                      ? {
                          left: `${
                            gridWrapperRef?.current?.getBoundingClientRect()
                              .right - 188
                          }px`,
                        }
                      : {}),
                    ...(isMobile &&
                    continueBtnRef?.current?.getBoundingClientRect().y
                      ? {
                          top:
                            mobileStickyTop ??
                            `${continueBtnRef?.current?.getBoundingClientRect()
                              .y}px`,
                          left: `${
                            gridWrapperRef?.current?.getBoundingClientRect()
                              .right - 164
                          }px`,
                        }
                      : {}),
                  }}
                  className={` md:top-[calc(100vh-132px)] fixed w-[140px]
    lg:top-[calc(100vh-160px)] z-[51]`}
                />
              )}
            <div
              data-testid="quoteDetails-test-id"
              className={` ${Styles.grid_block} grid md:grid-cols-12 grid-cols-6 gap-spacing-l xl:mx-auto lg:mx-[72px] md:mx-[48px] mx-[24px] relative`}
              ref={headerRef}
            >
              <div className={`${Styles.question_layout_wrapper} col-span-12`}>
                <div
                  className={` ${Styles.question_layout} bg-dark-surface-accent-1-primary max-h-full text-surface-neutral-option-2 rounded-radius-xxl min-h-[120px] relative`}
                >
                  <HelpOverLayButton />
                  <Logo
                    color="white"
                    heightClass="h-[80px]"
                    size="xl"
                    showondesktop={isdesktop.toString()}
                  />

                  {showInterstitial ? (
                    <InterstitialPageContent />
                  ) : (
                    <div
                      className={` ${Style.content_box}  ${
                        current?.uiElementControls?.internationalHeaderStyle
                          ? "lg:pt-spacing-xs md:pt-spacing-xs md:px-spacing-xl"
                          : " lg:pt-spacing-l md:pt-spacing-xl md:px-spacing-xxl"
                      } sm:pt-spacing-xs sm:pb-spacing-l ${contentBoxPaddingBottom} px-spacing-l lg:px-0 flex flex-col items-center`}
                    >
                      <QuoteHeader headerContent={headerContent} />
                    </div>
                  )}
                </div>
              </div>
            </div>
            {!showInterstitial && (
              <>
                <div className="max-w-[1200px] mx-auto w-[100%] relative">
                  <div
                    data-testid="quoteDetails-content-test-id"
                    className={` ${
                      Styles.grid_block
                    }  grid md:grid-cols-12 grid-cols-6 ${
                      isPromoBannerActive ? "lg:gap-spacing-l" : "gap-spacing-l"
                    } xl:mx-auto lg:mx-[72px] md:mx-[0px] mx-[0px] relative`}
                  >
                    {promoBannerContent &&
                      (() => {
                        const isWithTitle =
                          promoBannerContent.bannerType === "withTitle";

                        if (!isWithTitle) {
                          return (
                            <PromoCodeBanner
                              // Single Trip (ST) - Without Title
                              showBanner={promoBannerContent.showBanner}
                              discount={promoBannerContent.discount}
                              description={promoBannerContent.description}
                              promoCode={promoBannerContent.promoCode}
                              showTerms={promoBannerContent.showTerms}
                              termsText={promoBannerContent.termsText}
                              termsUrl={promoBannerContent.termsUrl}
                              showButton={promoBannerContent.showButton}
                              applyButtonLabel={
                                promoBannerContent.applyButtonLabel
                              }
                              appliedButtonLabel={
                                promoBannerContent.appliedButtonLabel
                              }
                              // Multi Trip (AMT) - Without Title
                              mtWithoutTitleShowBanner={
                                promoBannerContent.mtWithoutTitleShowBanner
                              }
                              mtWithoutTitleDiscount={
                                promoBannerContent.mtWithoutTitleDiscount
                              }
                              mtWithoutTitleDescription={
                                promoBannerContent.mtWithoutTitleDescription
                              }
                              mtWithoutTitlePromoCode={
                                promoBannerContent.mtWithoutTitlePromoCode
                              }
                              mtWithoutTitleShowTerms={
                                promoBannerContent.mtWithoutTitleShowTerms
                              }
                              mtWithoutTitleTermsText={
                                promoBannerContent.mtWithoutTitleTermsText
                              }
                              mtWithoutTitleTermsUrl={
                                promoBannerContent.mtWithoutTitleTermsUrl
                              }
                              mtWithoutTitleShowButton={
                                promoBannerContent.mtWithoutTitleShowButton
                              }
                              mtWithoutTitleApplyButtonLabel={
                                promoBannerContent.mtWithoutTitleApplyButtonLabel
                              }
                              mtWithoutTitleAppliedButtonLabel={
                                promoBannerContent.mtWithoutTitleAppliedButtonLabel
                              }
                              // Configuration
                              bannerStatus={promoBannerContent.bannerStatus}
                            />
                          );
                        }

                        return (
                          <PromoCodeBannerWithTitle
                            content={promoBannerContent}
                          />
                        );
                      })()}

                    <div className={` col-span-12`} ref={gridWrapperRef}>
                      <div
                        ref={quoteCoverRef}
                        className="md:!w-[100vw] lg:!w-auto"
                      >
                        <div>
                          <QuoteCover
                            quoteCoverContent={quoteCoverContent}
                            drawerData={drawerData}
                            quoteAreaData={quoteAreaData}
                            isPromoBannerActive={isPromoBannerActive}
                          />
                        </div>
                      </div>

                      {tripDetails !== null &&
                        !tripDetails?.initialQuotePageLoading && (
                          <div
                            ref={extrasSectionRef}
                            className={`${
                              isTaxCopyBelowButton
                                ? "mt-spacing-m"
                                : "mt-spacing-l"
                            }  md:mt-spacing-xl lg:mt-spacing-xxl`}
                          >
                            <AcoSection acoSectionContent={acoSectionContent} />
                          </div>
                        )}

                      {!shouldHideAtmToggle && (
                        <div className="mt-spacing-l md:mt-spacing-xl lg:mt-spacing-xxl">
                          <AtmToggleBottom
                            tripDetailsBottomAreaContent={
                              tripDetailsBottomAreaContent
                            }
                            toggleModalContent={toggleModalContent}
                          />
                        </div>
                      )}

                      {/* Price and doc  Area */}
                      {tripDetails !== null &&
                        !tripDetails?.initialQuotePageLoading && (
                          <div className="mt-spacing-l md:mt-spacing-xl lg:mt-spacing-xxl">
                            <PriceAndDocs
                              priceAndDocAreaContent={priceAndDocAreaContent}
                            />
                          </div>
                        )}
                    </div>
                  </div>
                </div>
                <div
                  ref={continueBtnRef}
                  className=" lg:mt-spacing-xxl md:mt-spacing-xl lg:max-w-[414px] md:max-w-[400px] mx-auto  mt-0 max-w-[unset] w-full fixed z-[1]  bottom-0 left-0  md:relative    flex flex-col justify-between h-fit"
                >
                  <ActionButton
                    disabledBtn2={
                      !quoteCompleted?.completed || isLoadingCoverState
                    }
                    disabledBtn1={isQuoteDisabled}
                    ctaButton2Text={ctaLabel}
                    onClickBtn1={prevOnClickHandler}
                    onClickBtn2={() => {
                      continueHandleClick();
                    }}
                    isLoading={continueIsLoading}
                    loadingIcon={<BtnLoadingSvg />}
                    id="quote"
                  />
                </div>
                <div className=" lg:mt-[-10px] md:mb-[40px] md:mt-[-30px] w-auto">
                  <QuestionFooterSection />
                </div>
                {productSpecialFeatures?.useNewHelpOverlay && (
                  <div className=" lg:mt-[-10px] md:mb-[40px] md:mt-[-30px] w-auto">
                    <CookiePolicyButton />
                  </div>
                )}
              </>
            )}
          </main>
          <FloatingChatButton
            chatButtonLabelMobile={chatButtonLabelMobile}
            chatButtonLabelDesktop={chatButtonLabelDesktop}
            show={productSpecialFeatures?.showNewChatButton}
          />
          <PtmExitModalWrapper ptmExitModalContent={ptmExitModalContent} />
        </div>
      </div>
    </div>
  );
}

export default withQuoteData(QuotePage);
